import g from '#core/global';
import events from '#core/events';
import config from '#core/config';
import load from '#core/load';
import cookie from '#core/cookie';
import util from '#core/util';
import hash from '#common/hash';

// TODO: eventually this should be refactored to use most of the same code as
// the alert, or vice versa

const cookie_prefix = 'modal_shown';
const cookie_value = '1';
const id_attr = 'id';
const click_delay_attr = 'data-modal-click-delay';
const page_delay_attr = 'data-modal-page-delay';
const days_attr = 'data-modal-days';
const idle_attr = 'data-modal-idle';
const is_active_attr = 'data-is-active';
const modal_$ = '[uk-modal]';

// Location-aware modal constants
const location_attr = 'data-modal-location';
const scroll_amount_attr = 'data-modal-scroll-amount';
const scroll_delay_attr = 'data-modal-scroll-delay';
const location_cookie_prefix = 'geo_location';
const scroll_delay_default = 3; // seconds after scroll threshold


let has_modal = false;
let user_has_interacted = false;

// Location-aware modal state
let user_location = null;
let scroll_threshold_reached = false;
let location_modal_shown = false;


const module = {
	
	/**
	 * Init module
	 */
	init() {
		if (load.ran('modal')) return false;
		
		load.content(() => {
			hash.setLinkAttribute(modal_$, false);
			has_modal = assign_elements();
			if (!has_modal) return false;
		});
		load.all(() => {
			has_modal = assign_elements();
			if (!has_modal) return false;
			
			start();
		});
	},
	
};

export default module;
module.init();


/**
 * Assign elements and event listeners
 * @return {boolean}
 */
function assign_elements() {
	if (has_modal) return true;
	
	const $modals = document.querySelectorAll(modal_$);
	if (!$modals.length) return false;
	
	document.addEventListener(events.uikit.before_show, e => {
		const $modal = e.target.closest(modal_$);
		if (!$modal) return;
		
		activate($modal);
	});
	
	document.addEventListener(events.uikit.hidden, e => {
		const $modal = e.target.closest(modal_$);
		if (!$modal) return;
		
		deactivate($modal);
	});
	
	['mousedown', 'touchstart'].forEach(event_type => {
		document.addEventListener(event_type, function click_listener(e) {
			user_has_interacted = true;
			document.removeEventListener(event_type, click_listener);
		});
	});
	
	return true;
}


/**
 * Start countdown to showing modal
 */
function start() {
	const $modals = document.querySelectorAll(modal_$);
	$modals.forEach($modal => {
		if (!$modal.hasAttribute(page_delay_attr)) return false;
		
		const delay = parseFloat($modal.getAttribute(page_delay_attr)) || 0;
		setTimeout(() => {
			show_on_timer($modal);
		}, parseInt(delay * 1000));
	});
	
	setTimeout(() => {
		activate_from_url();
	}, 250);
	
	// Initialize location-aware modals
	const $location_modals = document.querySelectorAll(`${modal_$}[${location_attr}]`);
	if ($location_modals.length) {
		init_location_modals($location_modals);
	}
}


/**
 * Show modal automatically after delay
 * @param {element} $modal
 */
function show_on_timer($modal) {
	const modal_id = $modal.getAttribute(id_attr);
	const cookie_name = get_cookie_name(modal_id);
	const days_between_showing = parseInt($modal.getAttribute(days_attr)) || 0;
	const show_only_when_idle = $modal.hasAttribute(idle_attr);
	const force_cookie = util.get_query('modal');
	
	if (
		(
			has_cookie(cookie_name)
			&& force_cookie != g.true_s
		) || (
			show_only_when_idle
			&& user_has_interacted
		)
	) return false;
	
	show($modal);
	cookie.create(cookie_name, cookie_value, days_between_showing);
}


/**
 * Check URL for target modal and activate
 */
function activate_from_url() {
	const url_hash = window.location.hash.substring(1);
	if (!url_hash.length) return false;
	
	const selector = `#${url_hash}${modal_$}`;
	if (!util.isValidSelector(selector)) return false;
	
	const $modal = document.querySelector(selector);
	if (!$modal) return false;
	
	// Remove modal ID from URL
	history.replaceState(null, null, '#');
	
	show($modal);
}


/**
 * Show modal
 * @param {element} $modal
 */
function show($modal) {
	const delay = $modal.getAttribute(click_delay_attr) || 0;
	setTimeout(() => {
		UIkit.modal(`#${$modal.id}`).show();
	}, delay * 1000);
}


/**
 * Activate modal before showing
 * @param {element} $modal
 */
function activate($modal) {
	$modal.setAttribute(is_active_attr, g.true_s);
}


/**
 * Deactivate modal after hiding
 * @param {element} $modal
 */
function deactivate($modal) {
	$modal.setAttribute(is_active_attr, g.false_s);
}


/**
 * Evaluate cookie to determine if modal should be shown
 * @param {string} cookie_name
 * @return {boolean}
 */
function has_cookie(cookie_name) {
	const modal_cookie = cookie.read(cookie_name);
	return modal_cookie === cookie_value;
}


/**
 * Get cookie name
 * @param {string} id
 * @return {string}
 */
function get_cookie_name(id) {
	return util.snake(`${config.site_name}_${cookie_prefix}_${id}`);
}


/**
 * Get location cookie name
 * @return {string}
 */
function get_location_cookie_name() {
	return util.snake(`${config.site_name}_${location_cookie_prefix}`);
}


/**
 * Initialize location-aware modals
 * @param {NodeList} $modals
 */
async function init_location_modals($modals) {
	user_location = await detect_location();
	if (!user_location) return;
	
	$modals.forEach($modal => {
		setup_scroll_listener($modal);
	});
}


/**
 * Detect user location via IPstack API
 * @return {object|null}
 */
async function detect_location() {
	// Check for existing location cookie first
	const cached = cookie.read(get_location_cookie_name());
	if (cached) {
		try {
			return JSON.parse(cached);
		} catch (e) {
			// Invalid cached data, continue to fetch
		}
	}
	
	// Fetch from IPstack
	const api_key = window.fritzScripts?.ipstack_key;
	if (!api_key) return null;
	
	try {
		const response = await fetch(`https://api.ipstack.com/check?access_key=${api_key}`);
		const data = await response.json();
		
		if (data.ip && data.country_code === 'US') {
			const location = {
				region_name: data.region_name,
				region_code: data.region_code
			};
			
			// Cache location in cookie
			const duration = window.fritzScripts?.geo_cookie_duration || 30;
			cookie.create(get_location_cookie_name(), JSON.stringify(location), duration);
			
			return location;
		}
	} catch (err) {
		console.warn('Location detection failed:', err);
	}
	
	return null;
}


/**
 * Set up scroll threshold listener for location modal
 * @param {element} $modal
 */
function setup_scroll_listener($modal) {
	const threshold = parseFloat($modal.getAttribute(scroll_amount_attr)) || 25;
	const scroll_delay = (parseFloat($modal.getAttribute(scroll_delay_attr)) || scroll_delay_default) * 1000;
	
	const check_scroll = util.throttle(() => {
		const doc_height = document.body.scrollHeight - window.innerHeight;
		if (doc_height <= 0) return;
		
		const scroll_percent = (window.scrollY / doc_height) * 100;
		
		if (scroll_percent >= threshold && !scroll_threshold_reached) {
			scroll_threshold_reached = true;
			window.removeEventListener('scroll', check_scroll);
			
			// Wait after threshold reached
			setTimeout(() => {
				show_location_modal($modal);
			}, scroll_delay);
		}
	}, 100);
	
	window.addEventListener('scroll', check_scroll);
}


/**
 * Show location-aware modal
 * @param {element} $modal
 */
function show_location_modal($modal) {
	if (location_modal_shown || !user_location) return;
	
	const modal_id = $modal.getAttribute(id_attr);
	const cookie_name = get_cookie_name(modal_id);
	const days_between_showing = parseInt($modal.getAttribute(days_attr)) || 0;
	const force_location = util.get_query('location');
	
	// Check if modal was already shown (unless forced)
	if (has_cookie(cookie_name) && force_location !== g.true_s) return;
	
	const current_region = window.fritzScripts?.current_state_site;
	const all_sites = window.fritzScripts?.all_sites || [];
	const modal_content = window.fritzScripts?.modal_content || {};
	
	// Skip if on national site
	if (current_region === 'national') return;
	
	// Check if user is in a different region
	const user_region = user_location.region_name.toLowerCase().replace(/ /g, '-');
	if (user_region === current_region?.toLowerCase()) return;
	
	// Find matching region site
	const site = all_sites.find(s =>
		s.state.toLowerCase().replace(/ /g, '-') === user_region
	);
	if (!site) return;
	
	// Apply location-specific content if available
	const region_content = modal_content[user_region] || modal_content[user_location.region_name];
	if (region_content) {
		apply_location_content($modal, region_content, user_location, site);
	} else {
		// Replace {region} placeholders
		replace_region_placeholders($modal, user_location.region_name, site.path);
	}
	
	location_modal_shown = true;
	show($modal);
	cookie.create(cookie_name, cookie_value, days_between_showing);
}


/**
 * Apply location-specific content from fritzScripts.modal_content
 * @param {element} $modal
 * @param {object} content - { title, body, link_text, link_url }
 * @param {object} location - { region_name, region_code }
 * @param {object} site - { state, path }
 */
function apply_location_content($modal, content, location, site) {
	const selectors = {
		title: '.modal-title, [data-modal-title]',
		body: '.modal-body, [data-modal-body]',
		link_text: '.modal-link, [data-modal-link]',
	};
	
	Object.entries(selectors).forEach(([key, selector]) => {
		if (!content[key]) return;
		const $el = $modal.querySelector(selector);
		if ($el) {
			$el.innerHTML = content[key]
				.replace(/\{region\}/g, location.region_name)
				.replace(/\{region_url\}/g, site.path);
		}
	});
	
	// Handle link URL separately
	if (content.link_url) {
		const $link = $modal.querySelector('.modal-link, [data-modal-link]');
		if ($link) {
			$link.href = content.link_url.replace(/\{region_url\}/g, site.path);
		}
	}
}


/**
 * Replace {region} placeholders in modal content
 * @param {element} $modal
 * @param {string} region_name
 * @param {string} region_url
 */
function replace_region_placeholders($modal, region_name, region_url) {
	// Find all text nodes and replace {region} placeholders
	const walker = document.createTreeWalker($modal, NodeFilter.SHOW_TEXT);
	while (walker.nextNode()) {
		if (walker.currentNode.textContent.includes('{region}')) {
			walker.currentNode.textContent = walker.currentNode.textContent
				.replace(/\{region\}/g, region_name);
		}
	}
	
	// Update links with {region_url} in href
	$modal.querySelectorAll('a[href*="{region_url}"]').forEach($link => {
		$link.href = $link.href.replace(/\{region_url\}/g, region_url);
	});
}
